// src/hooks/useAccessToken.ts

import { useAppSelector } from "@/store/hooks";

/**
 * Custom hook to access the centralized access token from Redux store
 *
 * This hook provides a single source of truth for authentication tokens,
 * eliminating the need for components to manage their own token state.
 *
 * Token lifecycle is managed by AuthReduxSync component which:
 * - Fetches token on login
 * - Stores it in Redux with expiry timestamp
 * - Automatically refreshes before expiry
 * - Clears it on logout
 *
 * @returns The current valid access token or undefined if not authenticated
 *
 * @example
 * const MyComponent = () => {
 *   const authToken = useAccessToken();
 *
 *   const { data } = useQuery(
 *     myQueryOptions(authToken)
 *   );
 *
 *   return <div>...</div>;
 * };
 */
export const useAccessToken = (): string | undefined => {
  const { accessToken, tokenExpiry, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );

  // Return undefined if not authenticated
  if (!isAuthenticated) {
    return undefined;
  }

  // Return undefined if token or expiry is not set
  if (!accessToken || !tokenExpiry) {
    return undefined;
  }

  // Check if token is expired (should not happen due to auto-refresh, but defensive check)
  const now = Date.now();
  if (now >= tokenExpiry) {
    console.warn("Access token has expired. Auto-refresh should have prevented this.");
    return undefined;
  }

  // Return the valid token
  return accessToken;
};

/**
 * Hook to check if the access token is expiring soon
 * Useful for UI indicators or proactive refresh triggers
 *
 * @param thresholdMinutes - Minutes before expiry to consider "expiring soon" (default: 5)
 * @returns true if token will expire within the threshold
 */
export const useIsTokenExpiringSoon = (thresholdMinutes: number = 5): boolean => {
  const { tokenExpiry, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );

  if (!isAuthenticated || !tokenExpiry) {
    return false;
  }

  const now = Date.now();
  const thresholdMs = thresholdMinutes * 60 * 1000;
  const timeUntilExpiry = tokenExpiry - now;

  return timeUntilExpiry <= thresholdMs && timeUntilExpiry > 0;
};
